import { useEffect, useState } from "react"
import PropTypes from "prop-types"
import { graphQueryBang } from "shared/lib/graphql"

import SearchInput from "./search_input"
import Spinner from "shared/components/spinner"
import { trackClickServiceSelected } from "./utils/telemetry"
import { partition, pluralizeStepType } from "shared/lib/utils"
import classnames from "classnames"

import "./service_selector.scss"
import SearchIndex from "shared/lib/search_index"
import EmptyStateCta from "shared/components/empty_state"

export default function ServiceSelector({
  loading,
  onServiceSelect,
  onServiceSelectQuery,
  services,
  allServiceCategories,
  stepType,
  updateLoading,
}) {
  const [matchingServices, updateMatchingServices] = useState([])
  const [unavailableServices, updateUnavailableServices] = useState([])
  const [serviceCategoryFilter, setServiceCategoryFilter] = useState("All")
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedCategoryServices, setSelectedCategoryServices] = useState([])

  const allServiceIds = services.map(s => s.id)
  const searchIndex = new SearchIndex()
  services.forEach(s => {
    searchIndex.add(s.id, s.name)
  })

  const handleCategoryChange = evt => {
    const category = evt.target.value

    if (searchTerm) setSearchTerm("") // reset search input when user first uses search bar and then selects from the category dropdown

    let visibleServices
    switch (category) {
      case "All":
        visibleServices = services
        break
      case "New":
        visibleServices = services.filter(s => s.tags.includes("new"))
        break
      case "Popular":
        visibleServices = services.filter(s => s.tags.includes("popular"))
        break
      default:
        visibleServices = services.filter(s => s.category?.name === category)
    }

    setServiceCategoryFilter(category)
    setSelectedCategoryServices(visibleServices)
    updateRenderedServices(visibleServices)
  }

  function updateRenderedServices(services) {
    const [matching, unavailable] = partition(
      services,
      service => service[`public_${pluralizeStepType(stepType)}`].length > 0
    )

    updateMatchingServices(matching)
    updateUnavailableServices(unavailable)
  }

  useEffect(() => {
    updateRenderedServices(services)
  }, [services])

  const updateSearchTerm = evt => {
    const term = evt.target.value

    if (!term) return clearSearch()

    setSearchTerm(term.trim())
    onSearchResult(searchIndex.search(term.trim()))
  }

  const clearSearch = () => {
    setSearchTerm("")
    onSearchResult(allServiceIds)
  }

  const onSearchResult = serviceIds => {
    let visibleServices

    if (serviceCategoryFilter != "All") {
      visibleServices = selectedCategoryServices.filter(service =>
        serviceIds.includes(service.id)
      )
    } else {
      visibleServices = services
        .filter(s => serviceIds.includes(s.id))
        .sort(
          (first, second) =>
            serviceIds.indexOf(first.id) - serviceIds.indexOf(second.id)
        )
    }

    updateRenderedServices(visibleServices)
  }

  const onClick = service => {
    if (loading) return null

    updateLoading(true)

    trackClickServiceSelected({
      serviceModuleName: service.module_name,
      stepType,
    })

    graphQueryBang(onServiceSelectQuery, {
      serviceModuleName: service.module_name,
    })
      .then(data => {
        onServiceSelect({ ...service, ...data.channel })
      })
      .catch(() => updateLoading(false))
  }

  const tiles = (services, linkable = true) =>
    services.map(service => {
      let detail = (
        <div>
          <img src={service.image} alt={service.name} title={service.name} />
          <span>{service.name}</span>
        </div>
      )
      if (linkable) {
        detail = (
          <a
            title={`Choose service ${service.name}`}
            onClick={() => onClick(service)}
          >
            {detail}
          </a>
        )
      } else {
        detail = (
          <span
            title={`${
              service.name
            } doesn't have any available ${pluralizeStepType(stepType)}`}
          >
            {detail}
          </span>
        )
      }
      return (
        <li
          key={service.module_name}
          styleName={classnames("service-tile", {
            "disabled-service-tile": !linkable,
          })}
          style={{ backgroundColor: service.brand_color }}
          role="listitem"
        >
          {detail}
        </li>
      )
    })

  return (
    <div styleName="service-selector">
      <>
        {loading || !services.length ? (
          <Spinner />
        ) : (
          <>
            <ServiceCategory
              allServiceCategories={allServiceCategories}
              handleCategoryChange={handleCategoryChange}
              serviceCategoryFilter={serviceCategoryFilter}
            />
            <SearchInput
              searchTerm={searchTerm}
              onChange={updateSearchTerm}
              clearSearch={clearSearch}
              placeholder={
                serviceCategoryFilter != "All" ? "Filter" : "Search services"
              }
            />
            {matchingServices.length > 0 ? (
              <>
                <ul styleName="service-grid" role="list">
                  {tiles(matchingServices)}
                </ul>
                {unavailableServices.length > 0 && (
                  <>
                    <h4 styleName="service-grid-title">
                      Services with no available {pluralizeStepType(stepType)}
                    </h4>
                    <ul styleName="service-grid" role="list">
                      {tiles(unavailableServices, false)}
                    </ul>
                  </>
                )}
              </>
            ) : (
              <EmptyStateCta
                heading="No results found"
                copy="Looks like we couldn't find any Service matches. Please try again."
                extraClass="diy-filter"
              />
            )}
          </>
        )}
      </>
    </div>
  )
}

ServiceSelector.propTypes = {
  loading: PropTypes.bool.isRequired,
  onServiceSelect: PropTypes.func.isRequired,
  onServiceSelectQuery: PropTypes.string.isRequired,
  services: PropTypes.arrayOf(
    PropTypes.shape({
      module_name: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      image: PropTypes.string,
      brand_color: PropTypes.string,
    })
  ).isRequired,
  allServiceCategories: PropTypes.array,
  stepType: PropTypes.oneOf(["trigger", "query", "action"]).isRequired,
  updateLoading: PropTypes.func.isRequired,
}

export const ServiceCategory = ({
  allServiceCategories,
  handleCategoryChange,
  serviceCategoryFilter,
}) => {
  return (
    <div className="service-category-container">
      <select
        className="service-category-dropdown"
        onChange={handleCategoryChange}
        value={serviceCategoryFilter}
      >
        <optgroup label="Filters">
          <option value="All">All services</option>
          <option value="New">New services</option>
          <option value="Popular">Popular services</option>
        </optgroup>
        <optgroup label="Categories">
          {allServiceCategories.map((category, idx) => {
            return (
              <option key={idx} value={category}>
                {category}
              </option>
            )
          })}
        </optgroup>
      </select>
    </div>
  )
}

ServiceCategory.propTypes = {
  allServiceCategories: PropTypes.array,
  handleCategoryChange: PropTypes.func,
  serviceCategoryFilter: PropTypes.string,
}
